import { useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/api";
import { messageFor } from "@/lib/errors";
import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import { getBondIndicationPdf, getBondSubmission } from "@/lib/brokerBonds";

// BRK-S133 — the Program Indication letter, fetched as BYTES and turned into a
// URL the viewer can render, open and save.
//
// WHY A FETCH AND NOT AN `<iframe src>`. The route is own-scoped and
// bearer-authenticated, and an iframe's own request carries no `Authorization`
// header — it would simply 401 and paint an empty frame, with nothing on screen
// saying why. So the SPA fetches the blob through the authed requester
// (inheriting the silent refresh-on-401) and hands the viewer an object URL.
//
// WHY IT WAITS ON THE ORDER, NOT ON THE ROUTE. `indicationAvailable` is its own
// axis on the preapproval block, because rendering the letter is a later step
// than deciding the program: an `approved` order can carry no PDF for a moment
// ("minting is best-effort and retried"). PMS-S306 is explicit about how a
// consumer should handle that — "read `preapproval.indicationAvailable` on the
// order view before offering the download rather than probing this route" — so
// while the flag is false this re-reads the ORDER and does not touch the PDF
// route at all. The honest rendering of that moment is "we're preparing your
// letter", and the flag flipping is what ends it.
//
// Lives in `lib/` like `useBondLink` and `useExtractedReview`, so the page file
// stays components-only for react-refresh and the lifecycle is testable with a
// scripted requester rather than an `AuthProvider`.

/** Timing knobs, mirroring `BondLinkPollOptions`: injected in tests, real
 *  otherwise. */
export interface IndicationPdfOptions {
  /** Default 3s. The letter renders server-side in seconds, not minutes. */
  intervalMs?: number;
  /** How many times to ask before giving up. Default 5 — about 15 seconds,
   *  after which a broker deserves a sentence rather than a spinner. */
  attempts?: number;
  /**
   * Bump to fetch again from scratch — the panel's "Check again".
   *
   * A counter in the dependency graph rather than a `reload()` callback,
   * because the reload has to tear down the in-flight loop and its cleanup, and
   * that is what an effect dependency already does. A callback would have to
   * re-implement the cancellation.
   */
  retryToken?: number;
  wait?: (ms: number) => Promise<void>;
}

/**
 * What the document panel renders from.
 *
 * FOUR STATES, and `preparing` is the one worth naming: it is NOT an error and
 * NOT a blank. It says the decision has landed and the letter has not, which is
 * a real and brief condition upstream, and the panel keeps a reassuring frame
 * on screen instead of an empty one.
 */
export type IndicationPdfState =
  | { status: "loading"; url: null }
  /** The letter is not there yet, and we are still asking. */
  | { status: "preparing"; url: null }
  /** Asked enough times, or the read failed for another reason. */
  | { status: "unavailable"; url: null; message: string }
  | {
      status: "ready";
      url: string;
      /**
       * What to call the file when the broker saves it — the SERVER's name
       * (`Program-Indication-SB-01578.pdf`), falling back to a local one only
       * if PMS sent no `Content-Disposition`.
       *
       * The server's name is the one that means something: it carries the
       * order number, and it is the same string an underwriter sees on the
       * document. A name built here from the order id would put a uuid in the
       * broker's downloads folder instead.
       */
      filename: string;
    };

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Fetch the indication letter for `orderId`, retrying while PMS says it is not
 * ready, and resolve to an object URL.
 *
 * `available` is the block's `indicationAvailable`, and it GATES the fetch: a
 * false flag re-reads the order instead of asking for bytes. That is PMS-S306's
 * instruction ("rather than probing this route"), and it is also the better
 * read — the flag is the server's own statement about whether a letter exists,
 * where a 404 on the route deliberately cannot tell "none minted" from "not
 * your order".
 *
 * TWO retryable failures, and both are the contract's:
 *   • **404** — "no letter has been minted … minting is best-effort so an
 *     approved one may still be retrying". The race this exists for: the flag
 *     was true and the mint landed a moment later. A 404 also sends the loop
 *     back to watching the FLAG, rather than hammering the route.
 *   • **502** — "the document store did not answer — retry", verbatim.
 *
 * Everything else — a 403, a 500, a network drop — is reported immediately:
 * retrying a real failure just delays the sentence the broker needs.
 *
 * The object URL is REVOKED on unmount and before a refetch. A blob URL keeps
 * its bytes alive as long as the document does, and a broker who walks a few
 * orders would otherwise leave several megabytes of PDF pinned in the tab.
 *
 * ⚠️ BOTH REQUESTERS MUST BE REFERENTIALLY STABLE. They are effect
 * dependencies, so a fresh function per render restarts the loop on every
 * render — which is an unbounded request loop, not a slow one. The auth
 * context's `authedRequest` and `authedBlob` are both `useMemo`'d, so every
 * real caller is safe; a test that constructs one inside its render callback
 * is not, and this is where that reads as a hang.
 */
export function useIndicationPdf(
  fetchBlob: AuthedBlobRequester,
  /** Reads the ORDER, to watch `indicationAvailable` rather than probe the
   *  letter's route (PMS-S306's own guidance). */
  request: AuthedRequester,
  orderId: string | undefined,
  available: boolean,
  options: IndicationPdfOptions = {},
): IndicationPdfState {
  const {
    intervalMs = 3000,
    attempts = 5,
    retryToken = 0,
    wait = sleep,
  } = options;
  const [state, setState] = useState<IndicationPdfState>({
    status: available ? "loading" : "preparing",
    url: null,
  });
  // The URL to revoke. A ref, not state: revoking is cleanup, and putting it in
  // the dependency graph would tear down the fetch that produced it.
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    if (orderId === undefined) return;
    let cancelled = false;

    const revoke = () => {
      if (urlRef.current !== null) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };

    // Whether the ORDER says a letter exists. Seeded from the block the caller
    // already had; re-read from the order while it says no.
    let minted = available;

    (async () => {
      for (let attempt = 0; attempt < Math.max(attempts, 1); attempt += 1) {
        if (!minted) {
          // Ask the ORDER, never the letter's route (PMS-S306). A failed read
          // is not news — the last known state is still the best answer — so
          // it degrades to "still preparing" and tries again.
          try {
            const order = await getBondSubmission(request, orderId);
            if (cancelled) return;
            minted = order.preapproval?.indicationAvailable === true;
          } catch {
            if (cancelled) return;
          }
          if (!minted) {
            setState({ status: "preparing", url: null });
            await wait(intervalMs);
            if (cancelled) return;
            continue;
          }
        }
        try {
          const file = await getBondIndicationPdf(fetchBlob, orderId);
          if (cancelled) return;
          revoke();
          const url = URL.createObjectURL(file.blob);
          urlRef.current = url;
          setState({
            status: "ready",
            url,
            // Only a fallback — and deliberately not the order id, which is a
            // uuid. `indication.pdf` matches the route, so a broker who saves
            // two letters gets `indication (1).pdf` rather than two uuids.
            filename: file.filename ?? "indication.pdf",
          });
          return;
        } catch (err) {
          if (cancelled) return;
          const status = err instanceof ApiError ? err.status : 0;
          // The contract's two retryable failures. Anything else is real.
          if (status !== 404 && status !== 502) {
            setState({
              status: "unavailable",
              url: null,
              message:
                err instanceof ApiError
                  ? messageFor(err)
                  : "We couldn’t open your indication letter.",
            });
            return;
          }
          // A 404 contradicts the flag, so stop trusting it and go back to
          // watching the order — the mint is still in flight.
          if (status === 404) minted = false;
          setState({ status: "preparing", url: null });
        }
        await wait(intervalMs);
        if (cancelled) return;
      }
      if (!cancelled) {
        setState({
          status: "unavailable",
          url: null,
          message:
            "Your indication letter is still being prepared. It will be ready shortly — you can carry on in the meantime.",
        });
      }
    })();

    return () => {
      cancelled = true;
      revoke();
    };
    // `available` SEEDS the loop and is deliberately not a dependency.
    //
    // It is a starting position, not a subscription: once running, the loop's
    // own order reads are a strictly fresher answer to the same question, so a
    // prop flipping false→true tells it nothing it is not already about to
    // learn — while restarting the effect would discard a letter already in
    // hand and re-fetch it. The broker's "Check again" is the one deliberate
    // restart, and it has its own token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchBlob, request, orderId, intervalMs, attempts, retryToken, wait]);

  return state;
}
